import { Fragment, useEffect, useMemo, useState } from 'react';
import type { AgentSpec } from '../types/global';

type AgentSpecTabKey = 'description' | 'original' | 'clarified' | 'detailed';

const SPEC_TABS: { key: AgentSpecTabKey; label: string; icon: string }[] = [
  { key: 'description', label: 'Description', icon: 'fa-id-card' },
  { key: 'original', label: 'Your request', icon: 'fa-quote-left' },
  { key: 'clarified', label: 'What this agent does', icon: 'fa-bullseye' },
  { key: 'detailed', label: 'Detailed spec', icon: 'fa-list-alt' },
];

// True when the spec carries any written content worth showing a brief for.
export function hasSpecContent(spec: AgentSpec | null | undefined): boolean {
  if (!spec) return false;
  return Boolean(
    (spec.description && spec.description.trim())
    || (spec.original && spec.original.trim())
    || (spec.clarified && spec.clarified.trim())
    || (spec.detailed && spec.detailed.trim()),
  );
}

// Render text that may contain `[+ … +]` inferred-addition markers. The marked
// spans — things the agent filled in that the user never asked for — render in
// blue so the user can review the additions at a glance. Plain text outside the
// markers renders normally, preserving line breaks.
function HighlightedText({ text }: { text: string }) {
  const parts = text.split(/\[\+([\s\S]*?)\+\]/g);
  return (
    <>
      {parts.map((part, i) =>
        // Odd indices are the captured inferred additions.
        i % 2 === 1 ? (
          <mark key={i} className="agent-spec-added">{part}</mark>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function tabText(spec: AgentSpec, key: AgentSpecTabKey): string {
  if (key === 'description') return (spec.description || '').trim();
  if (key === 'original') return (spec.original || '').trim();
  if (key === 'clarified') return (spec.clarified || '').trim();
  return (spec.detailed || '').trim();
}

// Full-canvas overlay showing the Agent Factory's written brief for the current
// diagram: a compact description, the user's original request, a clarified
// rewrite, and a detailed version that fills in the blanks (inferred bits in
// blue). Opened by the brief button above the diagram; its own "Back to diagram"
// button dismisses it.
export function AgentSpecView({ spec, onBack }: { spec: AgentSpec; onBack: () => void }) {
  const hasDetailedAdds = !!(spec.detailed && /\[\+[\s\S]*?\+\]/.test(spec.detailed));
  const tabs = useMemo(
    () => SPEC_TABS.filter((tab) => tabText(spec, tab.key)),
    [spec],
  );
  const [active, setActive] = useState<AgentSpecTabKey>('description');

  useEffect(() => {
    if (tabs.some((tab) => tab.key === active)) return;
    if (tabs[0]) setActive(tabs[0].key);
  }, [tabs, active]);

  const activeTab = tabs.find((tab) => tab.key === active) || tabs[0];
  const activeText = activeTab ? tabText(spec, activeTab.key) : '';
  const showLegend = activeTab?.key === 'detailed' && hasDetailedAdds;

  return (
    <div className="agent-spec-view" role="region" aria-label="Agent brief">
      <div className="agent-spec-head">
        <button
          type="button"
          className="agent-spec-back"
          onClick={onBack}
          title="Back to the diagram"
          aria-label="Back to the diagram"
        >
          <i className="fas fa-arrow-left" aria-hidden="true" /> Diagram
        </button>
        <span className="agent-spec-title">Agent brief</span>
      </div>
      {tabs.length ? (
        <div className="agent-spec-tabs" role="tablist" aria-label="Agent brief sections">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab?.key === tab.key}
              className={activeTab?.key === tab.key ? 'agent-spec-tab active' : 'agent-spec-tab'}
              onClick={() => setActive(tab.key)}
              title={tab.label}
            >
              <i className={`fas ${tab.icon}`} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="agent-spec-body">
        {activeTab ? (
          <section className="agent-spec-section">
            <h3>
              {activeTab.label}
              {showLegend ? (
                <span className="agent-spec-legend">
                  <span className="agent-spec-added-dot" aria-hidden="true" />
                  blue = filled in by the agent
                </span>
              ) : null}
            </h3>
            <p className={activeTab.key === 'original' ? 'agent-spec-text agent-spec-original' : 'agent-spec-text'}>
              {activeTab.key === 'original' ? activeText : <HighlightedText text={activeText} />}
            </p>
          </section>
        ) : null}
      </div>
    </div>
  );
}
